import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, Stack, Typography, Alert, Breadcrumbs, Link,
  Avatar, Menu, MenuItem, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsIcon from '@mui/icons-material/Groups';
import GridViewIcon from '@mui/icons-material/GridView';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PublicIcon from '@mui/icons-material/Public';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { useAuth } from '../context/AuthContext';
import { useMe } from '../context/MeContext';
import { AdminMenu, PlatformMenu } from './AdminMenu';
import { layout } from '../theme/tokens';
import { getImpersonationRequests } from '../api/saasApi';
import logo from '../assets/logo.png';

/**
 * The application frame: top bar on desktop, hamburger + drawer below `md`.
 *
 * The previous AppBar rendered its nav as `display: {xs:'none', md:'flex'}` with
 * no drawer behind it, so a phone or portrait tablet had *no navigation at all*.
 * Since clinicians use this in the room with the child, that was the single
 * biggest usability hole in the product.
 */

const initials = (name = '') => {
  const parts = String(name).trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

/** Route → breadcrumb trail. Replaces the floated "Home"/"Domains" buttons. */
const useBreadcrumbs = () => {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const labels = {
    dashboard: 'Dashboard',
    patient: 'Patients',
    domains: 'Domains',
    assess: 'Assessment',
    report: 'Report',
    history: 'History',
    users: 'Clinicians',
    roles: 'Roles',
    billing: 'Billing',
    clinic: 'Clinic settings',
    platform: 'Platform',
    tenants: 'Clinics',
    audit: 'Audit log',
  };

  const crumbs = [];
  let href = '';
  segments.forEach((seg, i) => {
    href += `/${seg}`;
    const label = labels[seg];
    // Skip bare ids — "/patient/42" reads as "Patients" then the page title.
    if (!label) return;
    crumbs.push({ label, href, last: i === segments.length - 1 });
  });

  return crumbs;
};

const AppShell = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { isAuthenticated, logout } = useAuth();
  const { me, can, isPlatformAdmin, isActiveMember } = useMe();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState(null);

  const crumbs = useBreadcrumbs();

  // Pending "support access" requests from Vqura platform admins. Clinic admins
  // who can approve see a banner on every page, so a request never sits unseen
  // on the Clinicians page. Re-checked on each navigation.
  const canApproveAccess = isAuthenticated && can('user:approve');
  const [supportRequests, setSupportRequests] = useState(0);
  useEffect(() => {
    if (!canApproveAccess) { setSupportRequests(0); return undefined; }
    let cancelled = false;
    getImpersonationRequests()
      .then((res) => { if (!cancelled) setSupportRequests((res.data?.data || []).length); })
      .catch(() => { /* banner is best-effort */ });
    return () => { cancelled = true; };
  }, [canApproveAccess, location.pathname]);

  // Safety net: if a dialog/menu was unmounted while open (e.g. navigating from
  // inside it), MUI can leave `overflow: hidden` on <body> and the next page
  // cannot scroll. After each route change, clear that lock when no modal is open.
  useEffect(() => {
    const id = window.setTimeout(() => {
      const modalOpen = document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');
      if (modalOpen) return;
      [document.body, document.documentElement].forEach((el) => {
        if (el.style.overflow === 'hidden') el.style.removeProperty('overflow');
        el.style.removeProperty('padding-right');
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  // Auth and onboarding screens own the full viewport.
  const bareRoutes = ['/', '/login', '/signup', '/join', '/forgot-password', '/reset-password', '/otp-verify'];
  const isBare = bareRoutes.includes(location.pathname);
  if (isBare) return children;

  const go = (to) => {
    setDrawerOpen(false);
    navigate(to);
  };

  const isCurrent = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  // Drawer mirrors every destination the desktop bar offers, admin items included —
  // otherwise mobile users silently lose access to half the product.
  const hasClinic = !!me?.tenant;
  const drawerSections = [
    {
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: <GridViewIcon /> },
        { to: '/patient', label: 'Patients', icon: <GroupsIcon /> },
      ],
    },
    {
      heading: 'Admin',
      items: hasClinic ? [
        can('user:view') && { to: '/users', label: 'Clinicians', icon: <PeopleIcon /> },
        can('role:view') && { to: '/roles', label: 'Roles & permissions', icon: <SecurityIcon /> },
        can('billing:view') && { to: '/billing', label: 'Billing', icon: <ReceiptLongIcon /> },
        can('tenant:settings') && { to: '/clinic', label: 'Clinic settings', icon: <SettingsIcon /> },
      ].filter(Boolean) : [],
    },
    {
      heading: 'Platform',
      items: isPlatformAdmin ? [
        { to: '/platform', label: 'Overview', icon: <PublicIcon /> },
        { to: '/platform/tenants', label: 'Clinics', icon: <GroupsIcon /> },
        { to: '/platform/users', label: 'Users', icon: <PeopleIcon /> },
        { to: '/platform/audit', label: 'Audit log', icon: <ReceiptLongIcon /> },
      ] : [],
    },
  ].filter((s) => s.items.length > 0);

  const subscription = me?.subscription;
  const showTrialBanner = isActiveMember && subscription?.status === 'Trialing';
  const showLapsedBanner = isActiveMember && !!me?.billing?.isSuspended;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop && isAuthenticated && (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label="Open navigation">
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/'}
            sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
          >
            <img src={logo} alt="Vqura" style={{ height: 32 }} />
          </Box>

          {isDesktop && isAuthenticated && isActiveMember && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {[
                { to: '/dashboard', label: 'Dashboard' },
                { to: '/patient', label: 'Patients' },
              ].map((item) => (
                <Button
                  key={item.to}
                  onClick={() => go(item.to)}
                  disableRipple
                  sx={{
                    color: isCurrent(item.to) ? 'primary.main' : 'text.secondary',
                    fontWeight: 600,
                    minHeight: 'auto',
                    py: 1,
                    px: 1.5,
                    borderRadius: 0,
                    borderBottom: '2px solid',
                    borderColor: isCurrent(item.to) ? 'primary.main' : 'transparent',
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: isCurrent(item.to) ? 'primary.main' : 'text.primary',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <AdminMenu />
              <PlatformMenu />
            </Stack>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated && (
            <>
              <IconButton onClick={(e) => setAccountAnchor(e.currentTarget)} aria-label="Account menu" size="small">
                <Avatar sx={{ width: 34, height: 34, bgcolor: 'grey.100', color: 'text.secondary', fontSize: 13, fontWeight: 700 }}>
                  {initials(me?.name)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={accountAnchor}
                open={Boolean(accountAnchor)}
                onClose={() => setAccountAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ px: 2, py: 1.5, minWidth: 220 }}>
                  <Typography variant="subtitle2" noWrap>{me?.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">{me?.email}</Typography>
                  {me?.tenant?.name && (
                    <Chip size="small" label={me.tenant.name} sx={{ mt: 1, bgcolor: 'grey.100' }} />
                  )}
                </Box>
                <Divider />
                <MenuItem onClick={() => { setAccountAnchor(null); logout(); navigate('/'); }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: layout.drawerWidth, boxSizing: 'border-box' } }}
      >
        <Toolbar>
          <img src={logo} alt="Vqura" style={{ height: 30 }} />
        </Toolbar>
        <Divider />
        {drawerSections.map((section, i) => (
          <Box key={section.heading || i}>
            {section.heading && (
              <Typography variant="overline" color="text.secondary" sx={{ px: 2.5, pt: 2, pb: 0.5, display: 'block' }}>
                {section.heading}
              </Typography>
            )}
            <List sx={{ px: 1 }}>
              {section.items.map((item) => (
                <ListItem key={item.to} disablePadding>
                  <ListItemButton
                    selected={isCurrent(item.to)}
                    onClick={() => go(item.to)}
                    sx={{ borderRadius: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: isCurrent(item.to) ? 'primary.main' : 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}>
                      {item.label}
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {i < drawerSections.length - 1 && <Divider />}
          </Box>
        ))}
      </Drawer>

      <Box component="main" sx={{ pt: `${layout.appBarHeight}px` }}>
        {showLapsedBanner && (
          <Alert severity="error" square sx={{ borderRadius: 0 }}>
            This clinic is suspended. Please contact Vqura support to restore access.
          </Alert>
        )}
        {supportRequests > 0 && location.pathname !== '/users' && (
          <Alert
            severity="warning"
            square
            sx={{ borderRadius: 0 }}
            action={<Button size="small" color="inherit" onClick={() => navigate('/users')}>Review</Button>}
          >
            {supportRequests === 1
              ? 'A Vqura support admin is asking to access your clinic.'
              : `${supportRequests} Vqura support access requests are waiting for your approval.`}
          </Alert>
        )}
        {showTrialBanner && !showLapsedBanner && (
          <Alert
            severity="info"
            square
            sx={{ borderRadius: 0 }}
            action={can('billing:manage') ? (
              <Button size="small" onClick={() => navigate('/billing')}>Upgrade</Button>
            ) : undefined}
          >
            You&apos;re on a free trial
            {subscription?.currentPeriodEnd && ` until ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}.
          </Alert>
        )}

        <Box sx={{ maxWidth: layout.contentMaxWidth, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          {crumbs.length > 1 && (
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
              {crumbs.map((c) => (
                c.last
                  ? <Typography key={c.href} variant="body2" color="text.primary">{c.label}</Typography>
                  : <Link key={c.href} component={RouterLink} to={c.href} variant="body2" color="text.secondary">{c.label}</Link>
              ))}
            </Breadcrumbs>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
